import { ChevronRight, Close } from "@mui/icons-material";
import {
  Alert, Box, Button, CircularProgress, Divider, Drawer,
  FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Typography
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CVCIQProductSnapshot } from "../../../interfaces/CloseTheLoop.interfaces";
import { THEME } from "../utils/theme";
import ProductSnapshotFeedbackDetails, { isActiveFeedbackStatus } from "./ProductSnapshotFeedbackDetails";

type SnapshotTheme = { Theme: string; DistinctFeedback: number; ThemeSummary?: string | null };
type SnapshotStatus = { Status: string; Count: number };
type SnapshotFeedback = { Id: string; Title: string; Status: string; Url: string };
type ParsedArray<T> = { items: T[]; error?: string };
type FeedbackSelection = { productName: string; label: string };
type ThemeSelection = { productName: string; themeName: string };
type ProductSnapshotsProps = {
  data: CVCIQProductSnapshot[] | undefined;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  timeframeLabel: string;
  tenantId: string;
  feedbackPeriod: string;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isTheme = (value: unknown): value is SnapshotTheme =>
  isObject(value) && typeof value.Theme === "string" && isCount(value.DistinctFeedback) &&
  (value.ThemeSummary === undefined || value.ThemeSummary === null || typeof value.ThemeSummary === "string");
const isStatus = (value: unknown): value is SnapshotStatus =>
  isObject(value) && typeof value.Status === "string" && isCount(value.Count);
const isFeedback = (value: unknown): value is SnapshotFeedback =>
  isObject(value) && [value.Id, value.Title, value.Status, value.Url].every((field) => typeof field === "string");

const parseArray = <T,>(value: string, field: string, guard: (item: unknown) => item is T): ParsedArray<T> => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    return { items: [], error: `${field} could not be read: invalid JSON.` };
  }
  if (!Array.isArray(parsed) || !parsed.every(guard)) {
    return { items: [], error: `${field} could not be read: invalid data format.` };
  }
  return { items: parsed };
};

const SnapshotThemeItem: React.FC<{
  theme: SnapshotTheme;
  productName: string;
  onReadMore: () => void;
}> = ({ theme, productName, onReadMore }) => {
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const summary = theme.ThemeSummary?.trim();

  useEffect(() => {
    const element = summaryRef.current;
    if (!element) return;
    const measure = () => setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [summary]);

  return (
    <Box component="li" sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: THEME.textSecondary }}>
        <Box component="span" sx={{ fontWeight: 800, color: "#092442" }}>{theme.Theme}</Box>
        {" "}&mdash; {theme.DistinctFeedback} {theme.DistinctFeedback === 1 ? "feedback item" : "feedback items"}
      </Typography>
      {summary && (
        <Typography ref={summaryRef} sx={{
          mt: 0.3, fontSize: 14, lineHeight: 1.55, color: THEME.textSecondary,
          display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2,
          overflow: "hidden", wordBreak: "break-word", whiteSpace: "pre-line"
        }}>{summary}</Typography>
      )}
      {summary && isTruncated && (
        <Button size="small" onClick={onReadMore} aria-haspopup="dialog"
          aria-label={`Read full summary for ${theme.Theme} in ${productName}`}
          sx={{ p: 0, minWidth: 0, mt: 0.35, textTransform: "none", fontSize: 12 }}>
          Read more
        </Button>
      )}
    </Box>
  );
};

const SignalCard: React.FC<{
  label: string;
  productName: string;
  total: number;
  statuses: ParsedArray<SnapshotStatus>;
  records: ParsedArray<SnapshotFeedback>;
  onView: (selection: FeedbackSelection) => void;
}> = ({ label, productName, total, statuses, records, onView }) => {
  const countsAvailable = isCount(total) && !statuses.error &&
    statuses.items.reduce((sum, item) => sum + item.Count, 0) === total;
  const active = statuses.items.reduce(
    (sum, item) => sum + (isActiveFeedbackStatus(item.Status) ? item.Count : 0), 0
  );
  const error = statuses.error || records.error ||
    (!countsAvailable ? "Active count unavailable: the status breakdown does not match the total." : undefined);

  return (
    <Box role="group" aria-label={`${productName} ${label}`} sx={{
      minWidth: 0, p: 1.75, bgcolor: "#F4F9FF", border: "1px solid #D6E6F7", borderRadius: 1,
      display: "flex", flexDirection: "column", alignItems: "stretch"
    }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0067B8" }}>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.6 }}>
        <Typography sx={{ fontSize: 32, fontWeight: 750, lineHeight: 1.2, color: "#0067B8" }}>
          {countsAvailable ? active : "Not available"}
        </Typography>
        {countsAvailable && <Typography sx={{ fontSize: 13, color: "#49627B" }}>Active</Typography>}
      </Stack>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mt: 1, mb: 1.25 }}>
        {statuses.items.map((item, index) => (
          <Typography key={`${item.Status}-${index}`} sx={{ fontSize: 12, color: "#49627B" }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{item.Count}</Box>{" "}
            {item.Status.trim() || "Unspecified"}
          </Typography>
        ))}
        {!statuses.error && statuses.items.length === 0 && total === 0 && (
          <Typography sx={{ fontSize: 12, color: "#49627B" }}>No feedback recorded</Typography>
        )}
      </Box>
      {error && <Alert severity="warning" sx={{ mb: 1, fontSize: 12 }}>{error}</Alert>}
      <Divider sx={{ mt: "auto", borderColor: "#D6E6F7" }} />
      <Button
        aria-label={`View all ${label} for ${productName}`}
        disabled={Boolean(records.error) || records.items.length === 0}
        onClick={() => onView({ productName, label })}
        endIcon={<ChevronRight />}
        sx={{ mt: 0.5, px: 0, justifyContent: "space-between", textTransform: "none", fontSize: 12 }}
      >
        View all {isCount(total) ? total : "Not available"}
      </Button>
    </Box>
  );
};

const ProductSnapshots: React.FC<ProductSnapshotsProps> = ({
  data, isFetching, isError, onRetry, timeframeLabel, tenantId, feedbackPeriod
}) => {
  const [productFilter, setProductFilter] = useState("");
  const [selection, setSelection] = useState<FeedbackSelection | null>(null);
  const [themeSelection, setThemeSelection] = useState<ThemeSelection | null>(null);
  const products = useMemo(() => (data ?? []).map((product) => ({
    ...product,
    themes: parseArray(product.feedbackThemes, "Feedback themes", isTheme),
    featureStatuses: parseArray(product.featureRequestStatuses, "Feature request statuses", isStatus),
    incidentStatuses: parseArray(product.icmStatuses, "IcM statuses", isStatus),
    features: parseArray(product.featureRequests, "Feature requests", isFeedback),
    incidents: parseArray(product.icMs, "IcMs", isFeedback)
  })).filter((product) =>
    product.featureRequestCount !== 0 || product.icmCount !== 0 ||
    product.themes.items.length > 0 || product.features.items.length > 0 || product.incidents.items.length > 0 ||
    product.featureStatuses.items.some(({ Count }) => Count > 0) ||
    product.incidentStatuses.items.some(({ Count }) => Count > 0) ||
    [product.themes, product.featureStatuses, product.incidentStatuses, product.features, product.incidents]
      .some(({ error }) => Boolean(error))
  ), [data]);
  const effectiveProductFilter = products.some(({ productName }) => productName === productFilter) ? productFilter : "";
  const visibleProducts = products.filter(({ productName }) => !effectiveProductFilter || productName === effectiveProductFilter);
  const selectedProduct = products.find(({ productName }) => productName === selection?.productName);
  const selectedRecords = selection?.label === "Feature Requests" ? selectedProduct?.features : selectedProduct?.incidents;
  const themeProduct = products.find(({ productName }) => productName === themeSelection?.productName);
  const selectedTheme = themeProduct?.themes.items.find(({ Theme }) => Theme === themeSelection?.themeName);

  return (
    <Box component="section" aria-label="Product Snapshots">
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography component="h3" sx={{ fontSize: 18, fontWeight: 750, color: "#092442" }}>
              Product Snapshots
            </Typography>
            {!isFetching && !isError && (
              <Typography sx={{ fontSize: 12, color: THEME.textSecondary }}>
                {visibleProducts.length} {visibleProducts.length === 1 ? "product" : "products"}
              </Typography>
            )}
          </Stack>
          <Typography sx={{ mt: 0.2, fontSize: 14, color: THEME.textSecondary }}>
            Customer feedback themes and active signals with a full status breakdown.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ width: { xs: "100%", sm: 290 } }} disabled={isFetching || isError}>
          <InputLabel id="snapshot-product-label" shrink>Product</InputLabel>
          <Select
            labelId="snapshot-product-label"
            label="Product"
            value={effectiveProductFilter}
            displayEmpty
            onChange={(event) => {
              setProductFilter(event.target.value);
              setSelection(null);
              setThemeSelection(null);
            }}
            sx={{ bgcolor: "#FFF", fontSize: 14 }}
          >
            <MenuItem value="">All products</MenuItem>
            {products.map(({ productName }) => <MenuItem key={productName} value={productName}>{productName}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      {isFetching ? (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={20} aria-label="Loading product snapshots" />
          <Typography sx={{ fontSize: 14 }}>Loading product snapshots...</Typography>
        </Stack>
      ) : isError ? (
        <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
          Product snapshots could not be loaded.
        </Alert>
      ) : visibleProducts.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: THEME.textSecondary }}>
          No product snapshots are available for the selected customer, product, and timeframe.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {visibleProducts.map((product) => (
            <Box key={product.productName} sx={{
              display: "grid", gridTemplateColumns: {
                xs: "minmax(0, 1fr)", md: "180px minmax(0, 1fr)", xl: "180px minmax(0, 1.2fr) minmax(0, 1fr)"
              },
              border: "1px solid #D6E2F0", borderRadius: 1, overflow: "hidden", bgcolor: "#FFF"
            }}>
              <Box sx={{
                p: 2.25, bgcolor: "#F7F9FB", display: "flex", alignItems: "center",
                borderRight: { md: "1px solid #E1E9F3" }
              }}>
                <Typography component="h4" sx={{ fontSize: 14, fontWeight: 800, color: "#092442", overflowWrap: "anywhere" }}>
                  {product.productName}
                </Typography>
              </Box>
              <Box sx={{ p: 2.25, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17395C" }}>
                  FEEDBACK THEMES
                </Typography>
                {product.themes.error ? <Alert severity="warning" sx={{ mt: 1 }}>{product.themes.error}</Alert> :
                  product.themes.items.length === 0 ? (
                    <Typography sx={{ mt: 1.25, fontSize: 14, color: THEME.textSecondary }}>
                      No feedback themes recorded for the selected period.
                    </Typography>
                  ) : (
                    <Box component="ul" sx={{ my: 1.25, pl: 2.1, color: "#7D9EBF" }}>
                      {product.themes.items.map((theme, index) => (
                        <SnapshotThemeItem key={`${theme.Theme}-${index}`} theme={theme}
                          productName={product.productName}
                          onReadMore={() => setThemeSelection({ productName: product.productName, themeName: theme.Theme })} />
                      ))}
                    </Box>
                  )}
              </Box>
              <Box sx={{
                p: 1.75, display: "grid", gap: 1.5, minWidth: 0,
                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
                gridColumn: { md: "1 / -1", xl: "auto" }, borderLeft: { xl: "1px solid #E1E9F3" },
                borderTop: { xs: "1px solid #E1E9F3", xl: "none" }
              }}>
                <SignalCard label="Feature Requests" productName={product.productName}
                  total={product.featureRequestCount} statuses={product.featureStatuses}
                  records={product.features} onView={setSelection} />
                <SignalCard label="IcMs" productName={product.productName}
                  total={product.icmCount} statuses={product.incidentStatuses}
                  records={product.incidents} onView={setSelection} />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
      <Drawer anchor="right"
        open={Boolean(selectedTheme?.ThemeSummary?.trim()) && !isFetching && !isError}
        onClose={() => setThemeSelection(null)}
        PaperProps={{
          role: "dialog", "aria-modal": true, "aria-labelledby": "snapshot-theme-summary-title",
          sx: { width: { xs: "100%", sm: 600, md: 720 }, maxWidth: "100vw", height: "100dvh", overflow: "hidden" }
        }}>
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography id="snapshot-theme-summary-title" component="h2"
                sx={{ fontSize: 22, fontWeight: 800, color: "#092442", overflowWrap: "break-word" }}>
                {selectedTheme?.Theme}
              </Typography>
              <Typography sx={{ mt: 0.6, fontSize: 13, color: THEME.textSecondary }}>
                {[themeProduct?.tenantName, themeProduct?.productName, timeframeLabel].filter(Boolean).join(" / ")}
              </Typography>
              <Typography sx={{ mt: 0.6, fontSize: 12, color: THEME.textSecondary }}>
                {selectedTheme?.DistinctFeedback} {selectedTheme?.DistinctFeedback === 1 ? "feedback item" : "feedback items"}
              </Typography>
            </Box>
            <IconButton aria-label="Close theme summary" onClick={() => setThemeSelection(null)} sx={{ alignSelf: "flex-start" }}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ p: 3, flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Typography sx={{ fontSize: 14, lineHeight: 1.7, color: THEME.textSecondary,
              whiteSpace: "pre-line", wordBreak: "break-word" }}>
              {selectedTheme?.ThemeSummary}
            </Typography>
          </Box>
        </Box>
      </Drawer>
      {selection && selectedRecords && !selectedRecords.error && !isFetching && !isError && (
        <ProductSnapshotFeedbackDetails
          key={`${selection.productName}|${selection.label}`}
          tenantId={tenantId}
          tenantName={selectedProduct?.tenantName ?? ""}
          productName={selection.productName}
          feedbackPeriod={feedbackPeriod}
          timeframeLabel={timeframeLabel}
          label={selection.label}
          recordIds={selectedRecords.items.map(({ Id }) => Id)}
          onClose={() => setSelection(null)}
        />
      )}
    </Box>
  );
};

export default ProductSnapshots;
